import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Grid,
  Pagination,
  CircularProgress,
  alpha,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  FormHelperText
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import EmailIcon from '@mui/icons-material/Email';
import GetAppIcon from '@mui/icons-material/GetApp';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SettingsIcon from '@mui/icons-material/Settings';
import DomainIcon from '@mui/icons-material/Domain';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import copy from 'copy-to-clipboard';

// Available email domains with support information
const EMAIL_DOMAINS = [
  { 
    value: 'gmail.com', 
    label: 'Gmail', 
    description: 'Google Mail',
    supportsDot: true,
    supportsPlus: true
  },
  { 
    value: 'outlook.com', 
    label: 'Outlook', 
    description: 'Microsoft Outlook',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'yahoo.com', 
    label: 'Yahoo', 
    description: 'Yahoo Mail',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'hotmail.com', 
    label: 'Hotmail', 
    description: 'Microsoft Hotmail',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'protonmail.com', 
    label: 'ProtonMail', 
    description: 'Secure Email',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'aol.com', 
    label: 'AOL', 
    description: 'AOL Mail',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'icloud.com', 
    label: 'iCloud', 
    description: 'Apple iCloud',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'mail.com', 
    label: 'Mail.com', 
    description: 'Free Webmail',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'zoho.com', 
    label: 'Zoho', 
    description: 'Business Email',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'yandex.com', 
    label: 'Yandex', 
    description: 'Russian Email Service',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'tutanota.com', 
    label: 'Tutanota', 
    description: 'Secure German Email',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'gmx.com', 
    label: 'GMX', 
    description: 'Global Mail Exchange',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'fastmail.com', 
    label: 'Fastmail', 
    description: 'Premium Email Service',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'hey.com', 
    label: 'HEY', 
    description: 'Basecamp Email Service',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'mailfence.com', 
    label: 'Mailfence', 
    description: 'Secure Belgian Email',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'runbox.com', 
    label: 'Runbox', 
    description: 'Norwegian Privacy Email',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'disroot.org', 
    label: 'Disroot', 
    description: 'Freedom-Respecting Service',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'posteo.de', 
    label: 'Posteo', 
    description: 'Sustainable Email Service',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'pm.me', 
    label: 'ProtonMail Short', 
    description: 'ProtonMail Short Domain',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'live.com', 
    label: 'Live', 
    description: 'Microsoft Live Mail',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'msn.com', 
    label: 'MSN', 
    description: 'Microsoft Network',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'startmail.com', 
    label: 'StartMail', 
    description: 'Private Dutch Email',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'thunderbird.net', 
    label: 'Thunderbird', 
    description: 'Mozilla Email Client',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'mac.com', 
    label: 'Mac.com', 
    description: 'Legacy Apple Email',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'me.com', 
    label: 'Me.com', 
    description: 'Apple Email Service',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'cock.li', 
    label: 'Cock.li', 
    description: 'Privacy-Focused Email',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'skiff.com', 
    label: 'Skiff', 
    description: 'End-to-End Encrypted Email',
    supportsDot: false,
    supportsPlus: true
  },
  { 
    value: 'mail.ru', 
    label: 'Mail.ru', 
    description: 'Russian Email Service',
    supportsDot: false,
    supportsPlus: false
  },
  { 
    value: 'rambler.ru', 
    label: 'Rambler', 
    description: 'Russian Search Portal Email',
    supportsDot: false,
    supportsPlus: false
  }
];

// Filter out domains that don't support either trick
const SUPPORTED_DOMAINS = EMAIL_DOMAINS.filter(domain => domain.supportsDot || domain.supportsPlus);

const DotTrickGenerator = () => {
  const theme = useTheme();
  const [username, setUsername] = useState('');
  const [variations, setVariations] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [generating, setGenerating] = useState(false);
  const [totalVariations, setTotalVariations] = useState(0);
  const [emailDomain, setEmailDomain] = useState('gmail.com');
  const [trickType, setTrickType] = useState('dot'); // 'dot', 'plus', or 'both'
  const [customTagsValue, setCustomTagsValue] = useState('tag,work,personal,shopping');
  
  const variationsPerPage = 30;

  // Get current domain info
  const currentDomain = EMAIL_DOMAINS.find(d => d.value === emailDomain) || EMAIL_DOMAINS[0];
  
  // Determine available trick types for the current domain
  const getAvailableTrickTypes = () => {
    const types = [];
    if (currentDomain.supportsDot) types.push('dot');
    if (currentDomain.supportsPlus) types.push('plus');
    if (currentDomain.supportsDot && currentDomain.supportsPlus) types.push('both');
    
    // If current selected trick is not supported, select first available
    if (!types.includes(trickType) && types.length > 0) {
      setTrickType(types[0]);
    }
    
    return types;
  };
  
  const availableTrickTypes = getAvailableTrickTypes();

  // Generate all possible variations of the username using selected trick method
  const generateVariations = (username) => {
    if (!username || username.includes('@')) {
      setError(`Please enter a valid username (without @${emailDomain})`);
      return [];
    }

    // Remove dots if they exist in the input
    const cleanUsername = username.replace(/\./g, '');
    
    if (!cleanUsername) {
      setError('Please enter a valid email username');
      return [];
    }
    
    setError('');
    
    const results = [];
    
    // Check which trick methods are available for the current domain
    const canUseDot = currentDomain.supportsDot && (trickType === 'dot' || trickType === 'both');
    const canUsePlus = currentDomain.supportsPlus && (trickType === 'plus' || trickType === 'both');
    
    if (!canUseDot && !canUsePlus) {
      setError(`${currentDomain.label} doesn't support dot or plus tricks.`);
      return [];
    }
    
    setGenerating(true);
    
    setTimeout(() => {
      // Generate dot variations if supported
      if (canUseDot) {
        const len = cleanUsername.length;
        const totalCombinations = Math.pow(2, len - 1);
        setTotalVariations(prev => prev + totalCombinations);
        
        if (totalCombinations > 10000) {
          setSnackbarMessage('Generating a large number of variations, this might take a moment...');
          setOpenSnackbar(true);
        }
        
        // Generate all possible dot permutations
        for (let i = 0; i < totalCombinations; i++) {
          let email = cleanUsername[0];
          
          // For each binary representation of i, place a dot if the bit is 1
          for (let j = 1; j < len; j++) {
            // Check if the j-1 bit of i is set
            if ((i >> (j - 1)) & 1) {
              email += '.';
            }
            email += cleanUsername[j];
          }
          
          results.push(email + '@' + emailDomain);
        }
      }
      
      // Generate plus variations if supported
      if (canUsePlus) {
        const tags = customTagsValue.split(',').map(tag => tag.trim()).filter(tag => tag);
        
        if (tags.length > 0) {
          tags.forEach(tag => {
            results.push(`${cleanUsername}+${tag}@${emailDomain}`);
          });
          
          setTotalVariations(prev => prev + tags.length);
        } else {
          // Add basic plus variation even if no tags are provided
          results.push(`${cleanUsername}+@${emailDomain}`);
          setTotalVariations(prev => prev + 1);
        }
      }
      
      setVariations(results);
      setGenerating(false);
      setPage(1);
      
      setSnackbarMessage(`Generated ${results.length.toLocaleString()} possible variations!`);
      setOpenSnackbar(true);
    }, 0);
    
    return results;
  };

  // Reset totalVariations before generating new variations
  useEffect(() => {
    setTotalVariations(0);
  }, [username, trickType, emailDomain, customTagsValue]);

  // Generate variations when username changes
  useEffect(() => {
    if (username) {
      generateVariations(username);
    } else {
      setVariations([]);
    }
  }, [username]);

  // Re-generate variations when domain or trick type changes
  useEffect(() => {
    if (username) {
      generateVariations(username);
    }
  }, [emailDomain, trickType]);

  const handleInputChange = (e) => {
    const value = e.target.value.trim();
    setUsername(value);
  };

  const handleDomainChange = (e) => {
    const newDomain = e.target.value;
    setEmailDomain(newDomain);
    
    // Update trick type based on what's available for the new domain
    const domain = EMAIL_DOMAINS.find(d => d.value === newDomain);
    if (domain) {
      if (!domain.supportsDot && trickType === 'dot') {
        if (domain.supportsPlus) {
          setTrickType('plus');
        }
      } else if (!domain.supportsPlus && trickType === 'plus') {
        if (domain.supportsDot) {
          setTrickType('dot');
        }
      } else if (!domain.supportsDot && !domain.supportsPlus && trickType === 'both') {
        if (domain.supportsDot) {
          setTrickType('dot');
        } else if (domain.supportsPlus) {
          setTrickType('plus');
        }
      }
    }
  };

  const handleTrickTypeChange = (e, newTrickType) => {
    if (newTrickType !== null) {
      setTrickType(newTrickType);
    }
  };

  const handleCustomTagsChange = (e) => {
    setCustomTagsValue(e.target.value);
    if (trickType === 'plus' || trickType === 'both') {
      // Re-generate variations when tags change
      if (username) {
        generateVariations(username);
      }
    }
  };

  const handleGenerateClick = () => {
    generateVariations(username);
  };

  const handleRandomClick = () => {
    if (variations.length > 0) {
      const randomIndex = Math.floor(Math.random() * variations.length);
      copyToClipboard(variations[randomIndex]);
    }
  };

  const copyToClipboard = (text) => {
    copy(text);
    setSnackbarMessage('Email copied to clipboard!');
    setOpenSnackbar(true);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const downloadAllVariations = () => {
    if (variations.length === 0) return;
    
    const content = variations.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${username}-${emailDomain}-variations.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    setSnackbarMessage('All variations downloaded!');
    setOpenSnackbar(true);
  };
  
  // Calculate current page variations
  const indexOfLastVariation = page * variationsPerPage;
  const indexOfFirstVariation = indexOfLastVariation - variationsPerPage;
  const currentVariations = variations.slice(indexOfFirstVariation, indexOfLastVariation);
  const totalPages = Math.ceil(variations.length / variationsPerPage);

  // Get trick info explanations
  const getTrickExplanation = () => {
    let explanation = '';
    
    if (trickType === 'dot') {
      explanation = 'The dot trick adds dots between letters of your username (example: j.ohn.doe@gmail.com).';
    } else if (trickType === 'plus') {
      explanation = 'The plus trick adds +tag after your username (example: johndoe+shopping@gmail.com).';
    } else if (trickType === 'both') {
      explanation = 'Using both dot and plus tricks to generate maximum variations.';
    }
    
    return explanation;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Decorative elements for fluent design */}
      <Box
        sx={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          top: '-50px',
          right: '-50px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, rgba(0,0,0,0) 70%)`,
          zIndex: -1,
          borderRadius: '50%',
          filter: 'blur(20px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '250px',
          height: '250px',
          bottom: '-70px',
          left: '-70px',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.15)} 0%, rgba(0,0,0,0) 70%)`,
          zIndex: -1,
          borderRadius: '50%',
          filter: 'blur(25px)',
        }}
      />
      
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box 
          sx={{ 
            position: 'relative', 
            zIndex: 1,
          }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            align="center" 
            gutterBottom
            color="primary"
            fontWeight="500"
            sx={{ 
              mb: 1.5,
              textShadow: '0 0 10px rgba(76, 194, 255, 0.3)',
              background: 'linear-gradient(45deg, #4cc2ff, #34d5eb)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.5px',
            }}
          >
            Email Generator By Drxm
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2.5,
              px: { xs: 1, sm: 3 },
              opacity: 0.9,
            }}
            align="center"
          >
            Generate variations of your email address using "dot" and "plus" tricks for services that support them.
            These variations all deliver to your main inbox, but can be used to track and filter emails.
          </Typography>
          
          <Box 
            sx={{ 
              mb: 2.5,
              px: { xs: 0, sm: 2 },
            }}
          >
            <Grid container spacing={1.5} alignItems="flex-start">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Email Username"
                  variant="outlined"
                  placeholder={`Enter your username (without @${emailDomain})`}
                  value={username}
                  onChange={handleInputChange}
                  error={Boolean(error)}
                  helperText={error}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography color="text.secondary">@{emailDomain}</Typography>
                      </InputAdornment>
                    ),
                    sx: {
                      '&.MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.05)',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.05)',
                    }
                  }}
                >
                  <InputLabel id="email-domain-label">Email Provider</InputLabel>
                  <Select
                    labelId="email-domain-label"
                    id="email-domain"
                    value={emailDomain}
                    onChange={handleDomainChange}
                    label="Email Provider"
                    startAdornment={
                      <InputAdornment position="start">
                        <DomainIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                      </InputAdornment>
                    }
                  >
                    {EMAIL_DOMAINS.map((domain) => (
                      <MenuItem 
                        key={domain.value} 
                        value={domain.value}
                        disabled={!domain.supportsDot && !domain.supportsPlus}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1">{domain.label}</Typography>
                            <Box>
                              {domain.supportsDot && (
                                <Tooltip title="Supports dot trick">
                                  <Chip 
                                    label="Dot" 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                    icon={<CheckIcon />}
                                    sx={{ mr: 0.5, height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} 
                                  />
                                </Tooltip>
                              )}
                              {domain.supportsPlus && (
                                <Tooltip title="Supports plus trick">
                                  <Chip 
                                    label="Plus" 
                                    size="small" 
                                    color="secondary" 
                                    variant="outlined"
                                    icon={<CheckIcon />}
                                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} 
                                  />
                                </Tooltip>
                              )}
                              {!domain.supportsDot && !domain.supportsPlus && (
                                <Tooltip title="Doesn't support email tricks">
                                  <Chip 
                                    label="No Support" 
                                    size="small" 
                                    color="error" 
                                    variant="outlined"
                                    icon={<ClearIcon />}
                                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} 
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary">{domain.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {currentDomain.supportsDot && currentDomain.supportsPlus ? 
                      `${currentDomain.label} supports both dot and plus tricks` : 
                      currentDomain.supportsDot ? 
                      `${currentDomain.label} supports dot trick only` : 
                      currentDomain.supportsPlus ? 
                      `${currentDomain.label} supports plus trick only` : 
                      `${currentDomain.label} doesn't support email tricks`
                    }
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          
          {/* Trick type selection section - moved directly after email input with no spacing */}
          <Box sx={{ mt: -2, px: { xs: 0, sm: 2 } }}>
            <Typography variant="subtitle2" sx={{ mb: 0.1, fontWeight: 500 }}>
              Select Trick Type:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <ToggleButtonGroup
                color="primary"
                value={trickType}
                exclusive
                onChange={handleTrickTypeChange}
                aria-label="Trick Type"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 2,
                    py: 0.75,
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(76, 194, 255, 0.2)',
                      boxShadow: '0 0 8px rgba(76, 194, 255, 0.3)'
                    }
                  }
                }}
              >
                <ToggleButton 
                  value="dot" 
                  disabled={!currentDomain.supportsDot || !availableTrickTypes.includes('dot')}
                >
                  Dot Trick
                </ToggleButton>
                <ToggleButton 
                  value="plus" 
                  disabled={!currentDomain.supportsPlus || !availableTrickTypes.includes('plus')}
                >
                  Plus Trick
                </ToggleButton>
                <ToggleButton 
                  value="both" 
                  disabled={!availableTrickTypes.includes('both')}
                >
                  Both Tricks
                </ToggleButton>
              </ToggleButtonGroup>
              
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0 }}>
                {getTrickExplanation()}
              </Typography>
            </Box>
            
            {(trickType === 'plus' || trickType === 'both') && currentDomain.supportsPlus && (
              <Box sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Custom Tags for Plus Trick (comma separated)"
                  variant="outlined"
                  placeholder="Enter tags like: work,personal,shopping,social"
                  value={customTagsValue}
                  onChange={handleCustomTagsChange}
                  helperText="These will be added after '+' in your email (e.g., username+work@domain.com)"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.05)',
                    }
                  }}
                />
              </Box>
            )}
            
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1, fontSize: 18, opacity: 0.7, color: theme.palette.primary.main }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Always generating all possible variations
              </Typography>
              {username && username.length > 8 && trickType === 'dot' && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', ml: 2 }}>
                  Note: Dot trick for "{username}" will create {Math.pow(2, username.length - 1).toLocaleString()} combinations.
                </Typography>
              )}
            </Box>
          </Box>
          
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 1.5,
              mb: 1.5,
              mt: 1.5,
              flexWrap: 'wrap'
            }}
          >
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={handleGenerateClick}
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              disabled={!username || generating || (!currentDomain.supportsDot && !currentDomain.supportsPlus)}
              sx={{
                py: 1,
                px: 3,
                minWidth: '180px',
                color: 'white',
                fontWeight: 600,
                fontSize: '1rem',
                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                '& .MuiSvgIcon-root': {
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))'
                },
                boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
              }}
            >
              {generating ? 'Generating...' : 'Generate Variations'}
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              color="secondary"
              onClick={handleRandomClick}
              startIcon={<ShuffleIcon />}
              disabled={variations.length === 0 || generating}
              sx={{
                py: 1,
                px: 3,
                color: theme.palette.secondary.light,
                fontWeight: 600,
                fontSize: '0.95rem',
                border: '2px solid',
                borderColor: 'rgba(255, 64, 129, 0.5)',
                textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                '&:hover': {
                  borderColor: theme.palette.secondary.main,
                  backgroundColor: 'rgba(255, 64, 129, 0.08)'
                },
                '& .MuiSvgIcon-root': {
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))'
                }
              }}
            >
              Random Variation
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              color="primary"
              onClick={downloadAllVariations}
              startIcon={<GetAppIcon />}
              disabled={variations.length === 0 || generating}
              sx={{
                py: 1,
                px: 3,
                color: theme.palette.primary.light,
                fontWeight: 600,
                fontSize: '0.95rem',
                border: '2px solid',
                borderColor: 'rgba(76, 194, 255, 0.5)',
                textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: 'rgba(76, 194, 255, 0.08)'
                },
                '& .MuiSvgIcon-root': {
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))'
                }
              }}
            >
              Download All
            </Button>
          </Box>
          
          {variations.length > 0 && !generating && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography 
                  variant="h6" 
                  component="h3"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.primary.main,
                  }}
                >
                  {variations.length.toLocaleString()} Email Variations:
                </Typography>
                
                {variations.length > variationsPerPage && (
                  <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                    Showing {indexOfFirstVariation + 1}-{Math.min(indexOfLastVariation, variations.length)} of {variations.length.toLocaleString()}
                  </Typography>
                )}
              </Box>
              
              <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                {currentVariations.map((email, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              opacity: 0.9
                            }}
                          >
                            {email}
                          </Typography>
                          <Tooltip title="Copy to clipboard">
                            <IconButton 
                              size="small" 
                              onClick={() => copyToClipboard(email)}
                              color="primary"
                              sx={{
                                backgroundColor: 'rgba(76, 194, 255, 0.1)',
                                '&:hover': {
                                  backgroundColor: 'rgba(76, 194, 255, 0.2)',
                                }
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Pagination 
                    count={totalPages} 
                    page={page} 
                    onChange={handleChangePage} 
                    color="primary" 
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        '&.Mui-selected': {
                          background: 'rgba(76, 194, 255, 0.2)',
                          boxShadow: '0 0 5px rgba(76, 194, 255, 0.3)',
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
          
          {generating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', my: 6 }}>
              <CircularProgress size={70} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Generating email variations...
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity="success" 
          sx={{ 
            width: '100%',
            backgroundColor: 'rgba(46, 125, 50, 0.9)',
            backdropFilter: 'blur(10px)',
            '& .MuiAlert-icon': {
              color: '#fff'
            },
            color: '#fff',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DotTrickGenerator; 